import { SxProps, Theme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import { TooltipProps } from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React, { FC, useEffect, useRef, useState } from "react";

interface EllipsisTooltipTextProps {
	text: string;
	// Suppress the tooltip regardless of overflow (e.g. a collapsed rail).
	tooltipDisabled?: boolean;
	placement?: TooltipProps["placement"];
	sx?: SxProps<Theme>;
}

// Single-line label that ellipsis-truncates and only shows a tooltip when the
// text is actually clipped. Overflow is re-measured on mount, on text change,
// and whenever the element resizes (e.g. a container opening/collapsing) via a
// ResizeObserver.
const EllipsisTooltipText: FC<EllipsisTooltipTextProps> = ({
	text,
	tooltipDisabled = false,
	placement = "top",
	sx,
}) => {
	const textRef = useRef<HTMLSpanElement | null>(null);
	const [isOverflowed, setIsOverflowed] = useState(false);

	useEffect(() => {
		const el = textRef.current;
		if (!el) {
			return;
		}
		const checkOverflow = () => {
			setIsOverflowed(el.scrollWidth > el.clientWidth);
		};
		checkOverflow();
		const resizeObserver = new ResizeObserver(checkOverflow);
		resizeObserver.observe(el);
		return () => resizeObserver.disconnect();
	}, [text]);

	const showTooltip = !tooltipDisabled && isOverflowed;

	return (
		<Tooltip
			title={showTooltip ? text : ""}
			placement={placement}
			disableInteractive
			disableHoverListener={!showTooltip}
		>
			<Typography ref={textRef} noWrap sx={sx}>
				{text}
			</Typography>
		</Tooltip>
	);
};

export default EllipsisTooltipText;
